import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllCategories, deleteCategory } from '../redux/slice/category.slice';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Folder, Search } from 'lucide-react';
import DataTable from '../components/DataTable';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

const Categories = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { categories, loading, error } = useSelector((state) => state.category);

    // Local State
    const [searchTerm, setSearchTerm] = useState('');

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    useEffect(() => {
        dispatch(getAllCategories());
    }, [dispatch]);

    const handleEdit = (id) => {
        navigate(`/categories/edit/${id}`);
    };

    const handleDelete = (id) => {
        setCategoryToDelete(id);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (categoryToDelete) {
            dispatch(deleteCategory(categoryToDelete));
            setDeleteModalOpen(false);
            setCategoryToDelete(null);
        }
    };

    // Filter Logic
    const filteredCategories = (categories || []).filter(category =>
        (category.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    const columns = [
        {
            header: 'Name',
            accessor: 'name',
            render: (cat) => <div className="font-semibold text-gray-900">{cat.name}</div>
        },
        {
            header: 'Description',
            accessor: 'description',
            render: (cat) => <div className="text-sm text-gray-500 line-clamp-2">{cat.description || '-'}</div>
        }
    ];

    if (loading) return (
        <div className="min-h-[400px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
    );

    return (
        <div className="max-w-[95%] mx-auto py-8">
            <div className="space-y-8">
                {/* Header Section */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div>
                        <h2 className="text-3xl font-serif font-bold text-gray-900">Categories</h2>
                        <p className="text-gray-500 mt-1">Organize your projects</p>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <StatCard
                        title="Total Categories"
                        value={categories?.length || 0}
                        icon={Folder}
                        color="bg-indigo-50 text-indigo-600"
                    />
                </div>

                {/* Toolbar */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-center">
                    <div className="relative w-full sm:max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm transition-all"
                        />
                    </div>

                    <Link
                        to="/categories/new"
                        className="flex items-center text-nowrap px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-all shadow-md hover:shadow-lg w-full sm:w-auto justify-center"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Category
                    </Link>
                </div>

                {/* Table */}
                <DataTable
                    columns={columns}
                    data={filteredCategories}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    showView={false}
                    error={error}
                />
            </div>

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Category"
                message="Are you sure you want to delete this category? This action cannot be undone."
            />
        </div>
    );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between transition-transform hover:scale-[1.02]">
        <div>
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`p-4 rounded-xl ${color}`}>
            <Icon className="w-6 h-6" />
        </div>
    </div>
);

export default Categories;
